import { defineStore } from 'pinia';
import { useDashboardStore } from '@/store/dashboard';

export const useNextCardStore = defineStore('nextcard', {
    state: () => ({
        loading: false,
        loaded: false,
        lastFetchedAt: null,
        showHidden: false,
        show524: true,
        showFiltersMenu: false,
        showExclusionsSidebar: false,
        hide524Callout: false,
        users: [],
        pageNumber: 0,
        pageSize: 12,
        searchTerm: '',
        filters: {
            programs: [],
            types: [],
            banks: [],
            spends: [], // Spend required (min_spend thresholds)
            fees: [], // Annual fee thresholds
            bonusCategories: [], // Bonus categories filter
            welcomeBonus: [], // Welcome bonus filter
        },
        activeNextCard: {},
        activeOffenders: {},
        acknowledgedIneligibleCards: [],
        benefitsByCardId: {},
        benefitsLoaded: false,
        benefitsLoading: false,
    }),
    getters: {
        activeUser(state) {
            let user = state.users.find((obj) => {
                return obj.show_user;
            });
            let deadUser = {
                next_cards: [],
                omitted_cards: {
                    omit_offenders: [],
                    five_twenty_four_count: 0,
                },
                filters: {
                    types: [],
                    programs: [],
                    banks: [],
                    spends: [],
                    card_types: [],
                    institutions: [],
                },
            };

            return user ? user : deadUser;
        },
        getActiveUser() {
            this.users.find((user) => {
                if (user.show_user) {
                    return user;
                }
            });
        },
        nextCards() {
            return this.activeUser.next_cards;
        },
        offenders() {
            return this.activeUser.omitted_cards.omit_offenders;
        },
        showFive24FilterCallout() {
            if (this.$cookies.get('shownFive24Filter')) {
                return false;
            }
            if (this.hide524Callout) {
                return false;
            }
            if (this.activeUser.omitted_cards.five_twenty_four_count < 5) {
                return true;
            }
            return false;
        },
        fiveTwentyFourCount() {
            return this.activeUser.omitted_cards.five_twenty_four_count;
        },
        // Apply every active filter (hidden, type, program, spend, fee, bank,
        // bonus category, welcome bonus) to the user's next cards. Pure getter
        // with no side effects — both `topCard` and `filteredNextCards` derive
        // from this so the top recommendation always honors the same filters as
        // the rest of the list.
        allFilteredNextCards(state) {
            return this.nextCards.filter((nc) => {
                let hidden = state.showHidden ? true : !nc.hidden;
                let five24 = true;
                /*let five24 = state.show524
                    ? true
                    : !nc.contributes_to_five_twenty_four;*/
                let types =
                    state.filters.types.length === 0 ||
                    state.filters.types.includes(nc.type);
                let programs =
                    state.filters.programs.length === 0 ||
                    state.filters.programs.includes(nc.program?.type || '');
                // Spend required (min_spend)
                let spendRequired =
                    state.filters.spends.length === 0 ||
                    state.filters.spends.some((e) => {
                        // null/undefined means the spend requirement is
                        // unknown (never entered), not $0 — it can't satisfy
                        // a "$X and below" cap. A real 0 (no spend required)
                        // still matches.
                        if (nc.min_spend === null || nc.min_spend === undefined)
                            return false;
                        const spend = Number(nc.min_spend);
                        if (Number.isNaN(spend)) return false;
                        const valNum = typeof e === 'number' ? e : Number(e);
                        if (Number.isNaN(valNum)) return false;
                        return spend <= valNum; // and below
                    });
                // Annual fee
                let feesAccepted =
                    (state.filters.fees?.length || 0) === 0 ||
                    state.filters.fees.some((e) => {
                        const fee = Number(nc.annual_fee ?? 0);
                        if (e === '500+') return fee >= 500; // $500+
                        const valNum = typeof e === 'number' ? e : Number(e);
                        if (valNum === 0) return fee === 0; // None
                        if (!Number.isNaN(valNum)) return fee <= valNum; // and below
                        return false;
                    });
                let banks =
                    state.filters.banks.length === 0 ||
                    (nc.issuing_bank &&
                        state.filters.banks.includes(
                            nc.issuing_bank.institution_code
                        ));

                // Bonus categories filter - use real spend_bonus_categories data
                let bonusCategories = true;
                if (state.filters.bonusCategories.length > 0) {
                    // Laravel sends snake_case: rewardscc_data not rewardsccData
                    const rewardsData = nc.rewardscc_data || nc.rewardsccData;

                    // If card has no rewards data, filter it out
                    if (!rewardsData || !rewardsData.spend_bonus_categories) {
                        bonusCategories = false;
                    } else {
                        let spendCategories =
                            rewardsData.spend_bonus_categories;

                        // Ensure it's an array
                        if (!Array.isArray(spendCategories)) {
                            if (typeof spendCategories === 'string') {
                                try {
                                    spendCategories =
                                        JSON.parse(spendCategories);
                                } catch (e) {
                                    spendCategories = [];
                                }
                            } else {
                                spendCategories = [];
                            }
                        }

                        const normalizeCategory = (name) => {
                            if (!name) return null;
                            const lowered = name.toLowerCase();

                            if (lowered.includes('air travel')) return 'travel';
                            if (lowered.includes('prepaid hotels'))
                                return 'hotels';
                            if (
                                lowered.includes('ihg') &&
                                lowered.includes('hotel')
                            )
                                return 'hotels';
                            if (
                                lowered.includes('united') &&
                                lowered.includes('airline')
                            )
                                return 'airlines';
                            if (
                                lowered === 'all travel (ultimate rewards)' ||
                                lowered === 'all travel'
                            )
                                return 'travel';
                            if (
                                lowered.includes('old navy') ||
                                lowered.includes('gap')
                            )
                                return 'shopping';
                            if (lowered.includes('starbucks'))
                                return 'coffee_shops';
                            if (
                                lowered === 'ridesharing' ||
                                lowered === 'lyft' ||
                                lowered === 'uber'
                            )
                                return 'ridesharing';
                            if (lowered === 'gas stations' || lowered === 'gas')
                                return 'gas_stations';
                            if (
                                lowered === 'grocery stores' ||
                                lowered === 'groceries'
                            )
                                return 'grocery_stores';
                            if (
                                lowered === 'dining' ||
                                lowered === 'restaurants'
                            )
                                return 'dining';
                            if (
                                lowered === 'streaming services' ||
                                lowered === 'streaming'
                            )
                                return 'streaming';
                            if (lowered === 'department stores')
                                return 'department_stores';
                            if (
                                lowered === 'transit' ||
                                lowered === 'public transit'
                            )
                                return 'transit';
                            if (lowered === 'tolls') return 'tolls';
                            if (lowered === 'travel') return 'travel';
                            if (lowered === 'hotels') return 'hotels';
                            if (lowered === 'airlines' || lowered === 'airfare')
                                return 'airlines';

                            return name
                                .toLowerCase()
                                .replace(/\s+/g, '_')
                                .replace(/[^a-z0-9_]/g, '');
                        };

                        // Check if any of the card's categories match the selected filters
                        bonusCategories = spendCategories.some((cat) => {
                            if (!cat.spendBonusCategoryName) return false;

                            // Normalize the category name to match filter values
                            const normalizedName = normalizeCategory(
                                cat.spendBonusCategoryName
                            );

                            return (
                                normalizedName &&
                                state.filters.bonusCategories.includes(
                                    normalizedName
                                )
                            );
                        });
                    }
                }

                // Welcome bonus filter (use server-side welcome_bonus_points when available)
                let welcomeBonusFilter = true;
                if (state.filters.welcomeBonus.length > 0) {
                    welcomeBonusFilter = false;

                    // Prefer numeric value from backend accessor
                    const pointsVal = Number(nc.welcome_bonus_points);
                    const hasNumeric =
                        Number.isFinite(pointsVal) && pointsVal > 0;

                    // Fallback: very light parsing if backend value missing
                    const fallbackParse = () => {
                        const welcomeOffer =
                            nc.welcome_offer || nc.rich_text || '';
                        const offerText = (welcomeOffer || '')
                            .toString()
                            .toLowerCase();
                        const m = offerText.match(
                            /(\d[\d,]*)\s*(points|miles|bonus)/i
                        );
                        return m ? parseInt(m[1].replace(/,/g, '')) : null;
                    };

                    const effectivePoints = hasNumeric
                        ? pointsVal
                        : (fallbackParse() ?? null);
                    const noBonus = !effectivePoints || effectivePoints <= 0;

                    state.filters.welcomeBonus.forEach((range) => {
                        if (range === 'None') {
                            if (noBonus) welcomeBonusFilter = true;
                        } else if (range === '80,000+ points') {
                            if (!noBonus && effectivePoints >= 80000)
                                welcomeBonusFilter = true;
                        } else {
                            // e.g. "10,000-24,999 points"
                            const rm = (range || '').match(
                                /(\d{1,3},?\d{3})-(\d{1,3},?\d{3})/
                            );
                            if (rm && !noBonus) {
                                const min = parseInt(rm[1].replace(/,/g, ''));
                                const max = parseInt(rm[2].replace(/,/g, ''));
                                if (
                                    effectivePoints >= min &&
                                    effectivePoints <= max
                                )
                                    welcomeBonusFilter = true;
                            }
                        }
                    });
                }

                return (
                    hidden &&
                    programs &&
                    types &&
                    spendRequired &&
                    feesAccepted &&
                    banks &&
                    bonusCategories &&
                    welcomeBonusFilter &&
                    five24
                );
            });
        },
        // The single top recommended card: the highest-ranked card that passes
        // every active filter. Derived from the same filtered set as the list
        // so filters like "spend required" are never bypassed here.
        topCard() {
            const filtered = this.allFilteredNextCards;

            return filtered.length > 0 ? filtered[0] : {};
        },
        // The remaining recommendations rendered below the top card.
        filteredNextCards() {
            return this.allFilteredNextCards.slice(1);
        },
        filteredNextCardsWithSearch() {
            const base = this.filteredNextCards || [];
            const q = (this.searchTerm || '').toString().trim().toLowerCase();
            if (!q) return base;

            return base.filter((nc) => {
                const title = (nc.title || '').toLowerCase();
                const bank = (nc.issuing_bank?.name || '').toLowerCase();
                const programTypeRaw = nc.program?.type?.toString() || '';
                const programType =
                    programTypeRaw === 'flexible_currency'
                        ? 'travel'
                        : programTypeRaw;
                const program = programType.toLowerCase();
                return (
                    title.includes(q) || bank.includes(q) || program.includes(q)
                );
            });
        },
        topCardMatchesSearch() {
            // No top card to show (e.g. every card was excluded by the active
            // filters) means there's nothing to render or count — gate the
            // TopCard and let the empty-state messaging take over.
            if (!this.topCard?.id) return false;

            const q = (this.searchTerm || '').toString().trim().toLowerCase();
            if (!q) return true;
            if (!this.topCard?.title) return false;

            const title = (this.topCard.title || '').toLowerCase();
            const bank = (this.topCard.issuing_bank?.name || '').toLowerCase();
            const programTypeRaw = this.topCard.program?.type?.toString() || '';
            const programType =
                programTypeRaw === 'flexible_currency'
                    ? 'travel'
                    : programTypeRaw;
            const program = programType.toLowerCase();

            return title.includes(q) || bank.includes(q) || program.includes(q);
        },
        ommitedCards() {
            return this.activeUser.omitted_cards;
        },
        paginatedNextCards(state) {
            let cards = this.filteredNextCards;
            const start = state.pageNumber * state.pageSize,
                end = start + state.pageSize;
            return cards.slice(start, end);
        },
        types() {
            return this.activeUser.filters.card_types;
        },
        programTypes() {
            return this.activeUser.filters.programs;
        },
        banks() {
            return this.activeUser.filters.institutions;
        },
        spends() {
            return this.activeUser.filters.spends;
        },
        pageCount(state) {
            let l = this.filteredNextCards.length,
                s = state.pageSize;
            return Math.ceil(l / s);
        },
    },
    actions: {
        async getNextCards(forceRefresh = false) {
            // Always kick off the benefits fetch so it loads even when the
            // cards list is served from the in-memory store (cache hit below)
            // or when a stale wallet-level cache served a payload with empty
            // card_benefits embedded.
            this.getNextCardsBenefits(forceRefresh);

            const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
            if (
                !forceRefresh &&
                this.loaded &&
                this.lastFetchedAt &&
                Date.now() - this.lastFetchedAt < CACHE_TTL
            ) {
                return;
            }

            this.loading = true;
            console.log('Next Card Request:', {
                endpoint: '/next-card',
                method: 'get',
                forceRefresh,
            });

            return window.ApiService.apiCall('/next-card', 'get')
                .then((response) => {
                    console.log('Next Card Response:', response);
                    this.loaded = true;
                    this.lastFetchedAt = Date.now();
                    if (response && response.data && response.data.users) {
                        this.users = response.data.users;
                    } else {
                        this.users = [];
                    }
                    this.loading = false;
                })
                .catch((response) => {
                    console.error('Next Card Error:', response);
                    this.loading = false;
                    return Promise.reject(response);
                });
        },
        async getNextCardsBenefits(forceRefresh = false) {
            if (this.benefitsLoading) {
                return;
            }
            if (!forceRefresh && this.benefitsLoaded) {
                return;
            }
            this.benefitsLoading = true;
            console.log('Next Card Benefits Request:', {
                endpoint: '/next-card/benefits',
                method: 'get',
                forceRefresh,
            });

            return window.ApiService.apiCall('/next-card/benefits', 'get')
                .then((response) => {
                    console.log('Next Card Benefits Response:', response);
                    this.benefitsByCardId = (response && response.data) || {};
                    this.benefitsLoaded = true;
                    this.benefitsLoading = false;
                })
                .catch((error) => {
                    console.error('Next Card Benefits Error:', error);
                    this.benefitsLoading = false;
                });
        },
        toggleFilter(filter, type) {
            if (this.filters[type].includes(filter)) {
                this.filters[type].splice(
                    this.filters[type].indexOf(filter),
                    1
                );
            } else {
                this.filters[type].push(filter);
            }
            this.pageNumber = 0;
        },
        loadNextCards(user) {
            this.clearFilters();
            setTimeout(() => {
                this.users.forEach((obj) => {
                    obj.show_user = false;
                });
                user.show_user = true;
            }, 100);
        },
        clearFilters() {
            this.filters.types = [];
            this.filters.programs = [];
            this.filters.banks = [];
            this.filters.spends = [];
            this.filters.fees = [];
            this.filters.bonusCategories = [];
            this.filters.welcomeBonus = [];

            // Add null/undefined checks to prevent TypeError
            if (this.types && Array.isArray(this.types)) {
                this.types.forEach((obj) => {
                    obj.checked = false;
                });
            }
            if (this.programTypes && Array.isArray(this.programTypes)) {
                this.programTypes.forEach((p) => {
                    p.checked = false;
                });
            }
            if (this.banks && Array.isArray(this.banks)) {
                this.banks.forEach((b) => {
                    b.checked = false;
                });
            }
            if (this.spends && Array.isArray(this.spends)) {
                this.spends.forEach((s) => {
                    s.checked = false;
                });
            }
            this.show524 = true;
            this.pageNumber = 0;
        },
        goodProgramName(type) {
            let programName = type;
            switch (type) {
                case 'flexible_currency':
                    programName = 'travel';
                    break;
                default:
                    programName = type;
            }

            if (!programName) return '';
            return programName.replace(/\w\S*/g, function (txt) {
                return (
                    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
                );
            });
        },
        toggleFiltersMenu() {
            this.showFiltersMenu = !this.showFiltersMenu;
        },
        gotoPage(p) {
            this.pageNumber = p;
        },
        nextPage() {
            this.pageNumber++;
        },
        prevPage() {
            this.pageNumber--;
        },
        setSearchTerm(term) {
            this.searchTerm = (term || '').toString();
            this.pageNumber = 0;
        },
        setShowHidden(showHidden) {
            this.showHidden = showHidden;
            this.pageNumber = 0;
        },
        setShow524(show) {
            this.show524 = show;
            this.pageNumber = 0;
        },
        setPageSize(val) {
            this.pageSize = val;
            this.pageNumber = 0;
        },
        setExclusionsSidebar(val) {
            this.showExclusionsSidebar = val;
            if (val) {
                document.body.classList.add('sidebar-open');
            } else {
                document.body.classList.remove('sidebar-open');
            }
        },
        setActiveNextCard(data) {
            this.activeNextCard = data.card;
            this.activeOffenders = data.offenders.hasOwnProperty(data.card.id)
                ? data.offenders[data.card.id]
                : {};
        },
        setFive24shownCookie() {
            this.hide524Callout = true;
            this.$cookies.set(
                'shownFive24Filter',
                '1',
                '365d',
                '/',
                window.location.hostname,
                true
            );
        },
        acknowledgeCardIneligibility(cardId) {
            if (!this.acknowledgedIneligibleCards.includes(cardId)) {
                this.acknowledgedIneligibleCards.push(cardId);

                const user = this.activeUser;
                if (user?.id) {
                    window.ApiService.apiCall(
                        `/user-settings/acknowledge-ineligible-card`,
                        'put',
                        {
                            uuid: user.uuid,
                            card_id: cardId,
                        }
                    ).catch((error) => {
                        console.error('Error acknowledging card:', error);
                    });
                }
            }
        },
        loadAcknowledgedCards() {
            const user = this.activeUser;
            if (user?.id) {
                window.ApiService.apiCall(
                    `/user-settings/acknowledged-cards`,
                    'get',
                    { uuid: user.uuid }
                )
                    .then((response) => {
                        if (
                            response.data &&
                            Array.isArray(response.data.acknowledged_cards)
                        ) {
                            this.acknowledgedIneligibleCards =
                                response.data.acknowledged_cards;
                        }
                    })
                    .catch(() => {});
            }
        },
        hideCardById(cardId) {
            const index = this.nextCards.findIndex(
                (card) => card.id === cardId
            );
            if (index !== -1) {
                const updatedCard = {
                    ...this.nextCards[index],
                    userHidden: true,
                };
                this.nextCards.splice(index, 1, updatedCard);
            }
        },

        setBonusCategories(categories) {
            this.filters.bonusCategories = categories;
        },
        setWelcomeBonus(bonuses) {
            this.filters.welcomeBonus = bonuses;
        },
    },
});
